import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Popconfirm, Avatar, Select, message, Modal, Checkbox, Spin, Tooltip } from 'antd';
import Table from '../../../shared/dataTable';
import { User, UserStatus, PermissionType, UserPermissions } from '../../../../types/user.types';
import { all_routes } from '../../../../router/all_routes';
import { UserOutlined, KeyOutlined } from '@ant-design/icons';
import { LoadingSpinner } from '../../../../utils/LoadingSpinner';
import { renderUserNameCell } from '../../../../utils/tableUtils';
import { getFileUrl } from '../../../../utils/file.utils';
import authService from '../../../../services/auth/auth.service';
import { useTranslation } from 'react-i18next';
import { handleApiError } from '../../../../utils/errorHandler';
import { RolesEnum } from '../../../../types/route.types';
interface EmployeeTableProps {
  users: User[];
  onEdit: (user: User) => void;
  onDelete: (id: number) => void;
  onStatusChange: (id: number, status: UserStatus) => void;
  onDeviceReset: (id: number) => void;
  onLeaveLimits?: (user: User) => void;
  loading?: boolean;
  currentUser: User;
  // Verilirse satır seçimi açılır (toplu işlem için)
  selectedIds?: number[];
  onSelectionChange?: (ids: number[]) => void;
}

const EmployeeTable: React.FC<EmployeeTableProps> = ({
  users,
  onEdit,
  onDelete,
  onStatusChange,
  onDeviceReset,
  onLeaveLimits,
  loading,
  currentUser,
  selectedIds,
  onSelectionChange
}) => {
  // Seçilen statü ve ID'yi saklamak için state'ler
  const [selectedStatus, setSelectedStatus] = useState<UserStatus | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const { t } = useTranslation();

  // İzinler için state'ler
  const [permissionModalVisible, setPermissionModalVisible] = useState(false);
  const [selectedUserForPermissions, setSelectedUserForPermissions] = useState<User | null>(null);
  const [userPermissions, setUserPermissions] = useState<UserPermissions>({ permissions: [] });
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('0');

  // Statü için etiketler
  const statusLabels: Record<string, string> = {
    [UserStatus.ACTIVE]: t('employees.statusOptions.working'),
    [UserStatus.RESIGNED]: t('employees.statusOptions.onLeave'),
    [UserStatus.DELETED]: t('employees.statusOptions.terminated'),
  };

  // Statü renkleri
  const getStatusColor = (status: UserStatus): string => {
    const colors: Record<string, string> = {
      [UserStatus.ACTIVE]: '#5cb85c',    // yeşil
      [UserStatus.RESIGNED]: '#6c757d',  // gri
      [UserStatus.DELETED]: '#d9534f',   // kırmızı
    };
    return colors[status] || '#6c757d';
  };

  // Statü badge'ı için stilize edilmiş sınıf
  const getStatusBadgeClass = (status: UserStatus): string => {
    switch (status) {
      case UserStatus.ACTIVE:
        return 'badge-success';
      case UserStatus.RESIGNED:
        return 'badge-secondary';
      case UserStatus.DELETED:
        return 'badge-danger';
      default:
        return 'badge-secondary';
    }
  };

  // Onay mesajını duruma göre döndüren fonksiyon
  const getConfirmMessage = (status: UserStatus): string => {
    switch (status) {
      case UserStatus.ACTIVE:
        return t('employees.statusOptions.popconfirmApprove');
      case UserStatus.RESIGNED:
        return t('employees.statusOptions.popconfirmReject');
      case UserStatus.DELETED:
        return t('employees.statusOptions.popconfirmDelete');
      default:
        return t('employees.statusOptions.popconfirmDefault');
    }
  };

  // Durum değişikliğini yönetme
  const handleStatusSelectChange = (userId: number, status: UserStatus) => {
    setSelectedUserId(userId);
    setSelectedStatus(status);
    setConfirmVisible(true);
  };

  // Durum değişikliğini onaylama
  const handleConfirm = () => {
    if (selectedUserId !== null && selectedStatus !== null) {
      onStatusChange(selectedUserId, selectedStatus);
      setConfirmVisible(false);
    }
  };

  // Durum değişikliğini iptal etme
  const handleCancel = () => {
    setSelectedUserId(null);
    setSelectedStatus(null);
    setConfirmVisible(false);
  };

  // Kullanıcı izinlerini getir
  const fetchUserPermissions = async (user: User) => {
    setSelectedUserForPermissions(user);
    setLoadingPermissions(true);
    try {
      const permissions = await authService.getUserPermissions(user.id);
      //console.log('Gelen izinler:', permissions);
      
      // API'dan gelen veri zaten doğru formatta olduğu için doğrudan kullanabiliriz
      // Eğer API'dan gelen veri UserPermissions tipinde değilse, uygun formata çevirelim
      if (permissions && 'permissions' in permissions) {
        setUserPermissions(permissions as UserPermissions);
      } else {
        // Eğer permissions doğrudan bir dizi ise veya farklı bir format gelirse
        setUserPermissions({ permissions: Array.isArray(permissions) ? permissions : [] });
      }
      setPermissionModalVisible(true);
    } catch (error: any) {
      handleApiError(error,t);
    } finally {
      setLoadingPermissions(false);
    }
  };

  // İzin değişikliğini ele al
  const handlePermissionChange = (permission: PermissionType, checked: boolean) => {
    if (checked) {
      setUserPermissions({
        permissions: [...userPermissions.permissions, permission]
      });
    } else {
      setUserPermissions({
        permissions: userPermissions.permissions.filter(p => p !== permission)
      });
    }
  };

  // İzinleri kaydet
  const savePermissions = async () => {
    if (!selectedUserForPermissions) return;
    
    setSavingPermissions(true);
    try {
      await authService.updateUserPermissions(selectedUserForPermissions.id, userPermissions.permissions);
      message.success('Kullanıcı izinleri başarıyla güncellendi');
      setPermissionModalVisible(false);
    } catch (error) {
      handleApiError(error,t);
    } finally {
      setSavingPermissions(false);
    }
  };

  // İzin adlarını daha okunabilir hale getir
  const getReadablePermissionName = (permission: string): string => {
    const permissionMap: Record<string, string> = {
      'control_panel_user': 'Kontrol Paneli Kullanıcısı',
      'control_panel_admin': 'Kontrol Paneli Yöneticisi',
      'view_users': 'Kullanıcıları Görüntüleme',
      'edit_users': 'Kullanıcıları Düzenleme',
      'view_departments': 'Departmanları Görüntüleme',
      'edit_departments': 'Departmanları Düzenleme',
      'view_branches': 'Şubeleri Görüntüleme',
      'edit_branches': 'Şubeleri Düzenleme',
      'export': 'Dışa Aktarma',
      'view_reports': 'Raporları Görüntüleme',
      'manage_shifts': 'Vardiyaları Yönetme',
      'leave_request': 'İzin Talebi',
      'view_leave': 'İzinleri Görüntüleme',
      'edit_leave': 'İzinleri Düzenleme',
      'manage_leave_hierarchy': 'İzin Onay Hiyerarşisi',
      'overtime': 'Fazla Mesai Yönetimi',
      'field_mode_requests': 'Saha Modu Talepleri Onayı',
      'create_notifications': 'Bildirim Oluşturma',
      'view_salaries': 'Maaşları Görüntüleme',
      'check_in_out': 'Giriş/Çıkış Yapma',
      'break': 'Mola',
      'company_settings': 'Şirket Ayarları',
      'file_upload': 'Dosya Yükleme',
      'file_view': 'Dosya Görüntüleme',
      'view_tickets_admin': 'Talepleri Görüntüleme (Yönetici)',
      'view_tickets_user': 'Talepleri Görüntüleme (Kullanıcı)',
      'edit_tickets': 'Talepleri Düzenleme',
      'view_assets': 'Zimmetleri Görüntüleme',
      'view_company_payment': 'Şirket Ödemelerini Görüntüleme',
      'view_company_timesheet': 'Şirket Puantaj Görüntüleme',
      'view_meals': 'Yemekleri Görüntüleme',
      'view_field_activities': 'Saha Aktivitelerini Görüntüleme',
      // 'create_field_activity': 'Saha Aktivitesi Oluşturma',
      // 'edit_field_activity': 'Saha Aktivitesi Düzenleme',
      // 'delete_field_activity': 'Saha Aktivitesi Silme',
      'view_vehicles': 'Araçları Görüntüleme',
      // 'manage_vehicles': 'Araç Yönetimi',
      'manage_referral_campaigns': 'Referans Programı Yönetimi',
      'manage_company': 'Şirket Yönetimi',
    };
    return permissionMap[permission] || permission;
  };

  // İzinleri kategorilere ayır
  const permissionCategories = [
    {
      title: 'Kullanıcı İzinleri',
      icon: 'ti ti-users',
      color: '#1890ff',
      permissions: [
        PermissionType.VIEW_USERS,
        PermissionType.EDIT_USERS,
      ]
    },
    {
      title: 'Organizasyon İzinleri',
      icon: 'ti ti-building',
      color: '#52c41a',
      permissions: [
        PermissionType.VIEW_DEPARTMENTS,
        PermissionType.EDIT_DEPARTMENTS,
        PermissionType.VIEW_BRANCHES,
        PermissionType.EDIT_BRANCHES,
        PermissionType.COMPANY
      ]
    },
    {
      title: 'Raporlama İzinleri',
      icon: 'ti ti-chart-bar',
      color: '#722ed1',
      permissions: [
        PermissionType.EXPORT,
        PermissionType.VIEW_REPORTS,
        PermissionType.VIEW_SALARIES,
        PermissionType.VIEW_COMPANY_PAYMENT,
        PermissionType.VIEW_COMPANY_TIMESHEET,
      ]
    },
    {
      title: 'İzin Yönetimi',
      icon: 'ti ti-calendar-event',
      color: '#fa8c16',
      permissions: [
        PermissionType.LEAVE_REQUEST,
        PermissionType.VIEW_LEAVE,
        PermissionType.EDIT_LEAVE,
        PermissionType.MANAGE_LEAVE_HIERARCHY,
      ]
    },
    {
      title: 'Vardiya Yönetimi',
      icon: 'ti ti-clock-hour-4',
      color: '#13c2c2',
      permissions: [
        PermissionType.MANAGE_SHIFTS,
        PermissionType.CHECK_IN_OUT,
        PermissionType.BREAK,
        PermissionType.OVERTIME,
      ]
    },
    {
      title: 'Saha Yönetimi',
      icon: 'ti ti-map-pin',
      color: '#eb2f96',
      permissions: [
        PermissionType.VIEW_FIELD_ACTIVITIES,
        // PermissionType.CREATE_FIELD_ACTIVITY,
        // PermissionType.EDIT_FIELD_ACTIVITY,
        // PermissionType.DELETE_FIELD_ACTIVITY,
        PermissionType.VIEW_VEHICLES,
        // PermissionType.MANAGE_VEHICLES,
        PermissionType.FIELD_MODE_REQUESTS,
      ]
    },
    {
      title: 'Talep Yönetimi',
      icon: 'ti ti-ticket',
      color: '#faad14',
      permissions: [
        PermissionType.VIEW_TICKETS_ADMIN,
        PermissionType.VIEW_TICKETS_USER,
        PermissionType.EDIT_TICKETS,
      ]
    },
    {
      title: 'Yemek Yönetimi',
      icon: 'ti ti-tools-kitchen-2',
      color: '#f5222d',
      permissions: [
        PermissionType.VIEW_MEALS,
      ]
    },
    {
      title: 'Referans Programı',
      icon: 'ti ti-gift',
      color: '#7338a1',
      permissions: [
        PermissionType.MANAGE_REFERRAL_CAMPAIGNS,
      ]
    },
    {
      title: 'Dosya & Zimmet',
      icon: 'ti ti-files',
      color: '#2f54eb',
      permissions: [
        PermissionType.FILE_UPLOAD,
        PermissionType.FILE_VIEW,
        PermissionType.VIEW_ASSETS,
      ]
    },
    {
      title: 'Diğer İzinler',
      icon: 'ti ti-settings',
      color: '#8c8c8c',
      permissions: [
        PermissionType.CREATE_NOTIFICATIONS,
      ]
    }
  ];

  // Column'ları dinamik oluştur
  const baseColumns = [
    {
      title: t('employees.table.name'),
      dataIndex: ["firstName", "lastName"],
      render: (_: string, record: User) => (
        <div className="d-flex align-items-center">
          {record.profilePicture ? (
            <Avatar src={getFileUrl(record.profilePicture)} className="me-2" size="large" />
          ) : (
            <Avatar src={record.gender === 'male' ? '/assets/img/man.png' : '/assets/img/woman.png'} className="me-2" size="large" />
          )}
          {renderUserNameCell(record.firstName, record)}
        </div>
      ),
      sorter: (a: User, b: User) => (a.firstName + a.lastName).localeCompare(b.firstName + b.lastName),
    },
    {
      title: t('employees.table.email'),
      dataIndex: "email",
      render: (email: string) => (
        <div className="d-flex align-items-center justify-content-between">
          <span>{email}</span>
          <button 
            className="btn btn-sm" 
            onClick={() => navigator.clipboard.writeText(email)}
            title={t('employees.table.copyEmail')}
          >
            <i className="ti ti-copy" />
          </button>
        </div>
      ),
      sorter: (a: User, b: User) => a.email.localeCompare(b.email),
    },
    {
      title: t('employees.table.department'),
      dataIndex: ["primaryDepartment", "name"],
      render: (_: string, record: User) => record.primaryDepartment?.name || '-',
      sorter: (a: User, b: User) => (a.primaryDepartment?.name || '').localeCompare(b.primaryDepartment?.name || ''),
    },
    {
      title: t('employees.table.branch'),
      dataIndex: ["branch", "name"],
      render: (_: string, record: User) => record.branch?.map(b => b.name).join(', ') || '-',
      sorter: (a: User, b: User) => (a.branch?.map(b => b.name).join(', ') || '').localeCompare(b.branch?.map(b => b.name).join(', ') || ''),
    },
    {
      title: t('employees.table.phone'),
      dataIndex: "phone",
      sorter: (a: User, b: User) => a.phone?.localeCompare(b.phone || '') || 0,
    },
    {
      title: 'Cihaz ID',
      dataIndex: "deviceId",
      render: (deviceId: string | null | undefined) => (
        deviceId
          ? <span title={deviceId}>{deviceId}</span>
          : <span className="text-muted">Eşleşmemiş</span>
      ),
      sorter: (a: User, b: User) => (a.deviceId || '').localeCompare(b.deviceId || ''),
    },
    {
      title: t('employees.table.status'),
      dataIndex: "status",
      render: (status: UserStatus, record: User) => {
        // Status değişikliği için dropdown seçenekleri
        const statusOptions = Object.values(UserStatus)
          .filter(statusOption => statusOption !== UserStatus.DELETED)
          .map(statusOption => ({
            value: statusOption,
            label: statusLabels[statusOption],
            disabled: statusOption === record.status
          }));

        return (
          <div className="d-flex align-items-center gap-2">
            <Select
              size="small"
              disabled={!currentUser.permissions?.includes(PermissionType.EDIT_USERS)}
              defaultValue={record.status}
              value={statusLabels[record.status]}
              style={{ 
                width: 120,
                fontSize: '0.8rem'
              }}
              onChange={(value) => handleStatusSelectChange(record.id, value as UserStatus)}
              options={statusOptions}
              className="status-dropdown"
              dropdownStyle={{ zIndex: 1100 }}
              bordered={true}
            />
            
            <Popconfirm
              title={t('employees.statusOptions.popconfirmStatus')}
              description={selectedStatus && selectedUserId === record.id ? getConfirmMessage(selectedStatus) : ''}
              open={confirmVisible && selectedUserId === record.id}
              onConfirm={handleConfirm}
              onCancel={handleCancel}
              okText={t('employees.statusOptions.popconfirmOk')}
              cancelText={t('employees.statusOptions.popconfirmCancel')}
              okButtonProps={{ 
                style: { 
                  backgroundColor: selectedStatus ? getStatusColor(selectedStatus) : undefined 
                } 
              }}
            >
              {/* Boş div - Popconfirm'i tetiklemek için gerekli */}
              <div />
            </Popconfirm>
          </div>
        );
      },
      sorter: (a: User, b: User) => String(a.status).localeCompare(String(b.status)),
    },
  ];

  // Actions column - sadece EDIT_USERS iznine sahipse ekle
  const actionsColumn = {
    title: t('employees.table.actions'),
    render: (_: any, record: User) => {
      const hasEditPermission = currentUser.permissions?.includes(PermissionType.EDIT_USERS);
      
      return (
        <div className="d-flex gap-2">
          {hasEditPermission && (
            <button
              className="btn btn-sm btn-primary"
              onClick={() => onEdit(record)}
              title={t('employees.statusOptions.edit')}
            >
              <i className="ti ti-edit" />
            </button>
          )}
          
          {hasEditPermission && currentUser.role === RolesEnum.ADMIN && (record.role === RolesEnum.MANAGER || record.role === RolesEnum.ADMIN) && record.status === UserStatus.ACTIVE && (
            <Tooltip title="İzinleri Düzenle">
              <button
                className="btn btn-sm btn-linear-primary text-white"
                onClick={() => fetchUserPermissions(record)}
                title="İzinleri Düzenle"
              >
                <i className="ti ti-key" />
              </button>
            </Tooltip>
          )}
          
          {hasEditPermission && onLeaveLimits && (
            <Tooltip title="İzin Limitleri">
              <button
                className="btn btn-sm btn-outline-success"
                onClick={() => onLeaveLimits(record)}
                title='İzin Limitlerini Düzenle'
              >
                <i className="ti ti-calendar-time" />
              </button>
            </Tooltip>
          )}
     
          {hasEditPermission && (
            <Popconfirm
              title={t('employees.statusOptions.deviceReset')}
              description={t('employees.statusOptions.deviceResetConfirm') || 'Bu cihazı sıfırlamak istediğinize emin misiniz?'}
              okText={t('employees.statusOptions.popconfirmOk')}
              cancelText={t('employees.statusOptions.popconfirmCancel')}
              okButtonProps={{
                style:{backgroundColor: '#7338a0'}
              }}
              onConfirm={() => {
                onDeviceReset(record.id);
              }}
              onCancel={handleCancel}
            >
              <button
                className="btn btn-sm btn-outline-primary"
                title={t('employees.statusOptions.deviceReset')}
              >
                <i className="ti ti-reload" />
              </button>
            </Popconfirm>
          )}
        </div>
      );
    },
  };

  // EDIT_USERS iznine sahipse actions column'u ekle
  const columns = currentUser.permissions?.includes(PermissionType.EDIT_USERS)
    ? [...baseColumns, actionsColumn]
    : baseColumns;

  // İzin modal içeriği
  const renderPermissionModal = () => {
    if (!selectedUserForPermissions) return null;

    const totalPermissions = permissionCategories.flatMap(c => c.permissions).length;
    const selectedPermissionsCount = userPermissions.permissions.length;

    return (
      <Modal
        title={
          <div className="d-flex align-items-center">
            <KeyOutlined className="me-3" style={{ fontSize: '20px', color: '#1890ff' }} />
            <div>
              <h5 className="mb-0">{t('employees.permissions.title', 'Kullanıcı İzinleri')}</h5>
              <small className="text-muted">
                {`${selectedUserForPermissions.firstName} ${selectedUserForPermissions.lastName}`}
              </small>
            </div>
          </div>
        }
        open={permissionModalVisible}
        onCancel={() => setPermissionModalVisible(false)}
        width={1000}
        bodyStyle={{ padding: '24px', maxHeight: '70vh', overflowY: 'auto' }}
        footer={
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-success"
                onClick={() => {
                  const allPermissions = permissionCategories.flatMap(c => c.permissions);
                  setUserPermissions({ permissions: allPermissions });
                }}
              >
                <i className="ti ti-check me-1"></i>
                Tümünü Seç
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => {
                  setUserPermissions({ permissions: [] });
                }}
              >
                <i className="ti ti-x me-1"></i>
                Temizle
              </button>
            </div>
            <button
              type="button"
              onClick={savePermissions}
              className="btn btn-primary"
              disabled={savingPermissions}
            >
              {savingPermissions ? (
                <>
                  <Spin size="small" className="me-2" /> 
                  {t('employees.permissions.saving', 'Kaydediliyor...')}
                </>
              ) : (
                <>
                  <i className="ti ti-device-floppy me-1"></i>
                  {t('employees.permissions.save', 'Kaydet')}
                </>
              )}
            </button>
          </div>
        }
      >
        {loadingPermissions ? (
          <div className="text-center py-5">
            <Spin size="large" tip={t('employees.permissions.loading', 'İzinler yükleniyor...')} />
          </div>
        ) : (
          <div className="permission-container">
            <div className="row g-3">
              {permissionCategories.map((category, categoryIndex) => {
                const categoryPermCount = category.permissions.filter(p => 
                  userPermissions.permissions.includes(p as PermissionType)
                ).length;
                const isCategoryFullySelected = categoryPermCount === category.permissions.length;
                
                return (
                  <div key={categoryIndex} className="col-md-6 col-lg-4">
                    <div className="card h-100" style={{ borderRadius: '8px' }}>
                      {/* Category Header */}
                      <div 
                        className="card-header d-flex align-items-center justify-content-between"
                        style={{ 
                          background: '#f8f9fa',
                          borderBottom: '1px solid #e9ecef',
                          padding: '12px 16px'
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <i 
                            className={category.icon} 
                            style={{ 
                              fontSize: '18px', 
                              marginRight: '8px',
                              color: category.color 
                            }}
                          ></i>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: '13px' }}>
                              {category.title}
                            </div>
                            <small style={{ fontSize: '11px', color: '#8c8c8c' }}>
                              {categoryPermCount}/{category.permissions.length} seçili
                            </small>
                          </div>
                        </div>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`category-${categoryIndex}`}
                          style={{ 
                            width: '18px', 
                            height: '18px',
                            cursor: 'pointer'
                          }}
                          onChange={(e) => {
                            if (e.target.checked) {
                              const newPermissions = [...userPermissions.permissions];
                              category.permissions.forEach(perm => {
                                if (!newPermissions.includes(perm)) {
                                  newPermissions.push(perm);
                                }
                              });
                              setUserPermissions({ permissions: newPermissions });
                            } else {
                              setUserPermissions({
                                permissions: userPermissions.permissions.filter(
                                  p => !category.permissions.includes(p)
                                )
                              });
                            }
                          }}
                          checked={isCategoryFullySelected}
                        />
                      </div>

                      {/* Category Body */}
                      <div className="card-body p-3">
                        {category.permissions.map((permission, permIndex) => {
                          const isChecked = userPermissions.permissions.includes(permission as PermissionType);
                          return (
                            <div 
                              key={`${permission}-${permIndex}`} 
                              className="mb-2"
                            >
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`perm-${permission}`}
                                  checked={isChecked}
                                  onChange={(e) => {
                                    handlePermissionChange(permission as PermissionType, e.target.checked);
                                  }}
                                />
                                <label 
                                  className="form-check-label" 
                                  htmlFor={`perm-${permission}`} 
                                  style={{ 
                                    fontSize: '13px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  {getReadablePermissionName(permission)}
                                </label>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Modal>
    );
  };

  return (
    <>
      <Table 
        dataSource={users} 
        columns={columns}
        scroll={{ x: 'max-content' }}
        rowKey="id"
        Selection={!!onSelectionChange}
        selectedRowKeys={onSelectionChange ? selectedIds ?? [] : undefined}
        onSelectionChange={onSelectionChange ? (keys) => onSelectionChange(keys.map(Number)) : undefined}
      />
      {renderPermissionModal()}
    </>
  );
};

export default EmployeeTable; 